import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild, HostListener } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { GridApi, RowNode, SelectionChangedEvent } from 'ag-grid-community';
import * as jquery from 'jquery';
import * as lo_ from 'lodash';
import * as _ from 'underscore';
import { isArray, isBoolean, isNumber, isString, isUndefined } from '../../models/utils';
import { DeferredPromise } from '../../models/deferred-promise';
import { DocumentFormat, DocumentViewerContext, DocumentViewerMetadataField, DrDocument, EmailThreadDocument } from '../../models/dr-document';
import { DrRequestInfo } from '../../models/dr-request-info';
import { DocumentListTabType, Endpoint, EntityType, HelpIndex, TagScope, TagState, MessageType, MetadataTokenizationType, LocalStorageKey } from '../../models/enums';
import { AlignDirection, CellActionType, CellTemplateType, GenericColDef, GridContext, FilterTemplateType } from '../../models/grid-context';
import { ActionType, ObjectActionListType, Permissions } from '../../models/permission';
import { Tag, TagApplication, TagDocumentsOperation, TagQueueApplication, TagQueueItem } from '../../models/tag';
import { ViewType } from '../../models/view';
import { CommonService } from '../../services/common/common.service';
import { RestService } from '../../services/rest/rest.service';
import { XMLService } from '../../services/xml/xml.service';
import { CustomSelectItem } from '../custom-select/custom-select.component';
import { DocumentListComponent } from '../document-list/document-list.component';
import { GenericGridComponent } from '../generic-grid/generic-grid.component';
import { GenericTreeNode } from '../generic-tree/generic-tree-node';
import { AddSelectedTextToModalComponent, AddSelectedTextToModalData, AddSelectedTextToModalType } from '../modals/add-selected-text-to-modal/add-selected-text-to-modal.component';
import { DeleteEntityModalComponent } from '../modals/delete-entity-modal/delete-entity-modal.component';
import { EditBucketModalComponent, EditMode } from '../modals/edit-bucket/edit-bucket-modal.component';
import { PagerComponent } from '../pager/pager.component';
import { ResizerComponent } from '../resizer/resizer.component';
import { formatNumber } from '@angular/common';
import { Subscription } from 'rxjs';
import { HttpService } from '../../services/http/http.service';
import { MetadataToken, MetadataService } from '../../services/metadata/metadata.service';
import { DynamicFolder } from '../../models/dynamic-folder';

declare let Hilitor: any;
import Mark from 'mark.js';
@Component({
    standalone: false,
    selector: 'res-document-viewer',
    templateUrl: './document-viewer.component.html',
    styleUrls: ['./document-viewer.component.scss']
})
export class DocumentViewerComponent implements OnInit, OnDestroy {
    @ViewChild('leftResizer', { static: true }) leftResizer: ResizerComponent;
    @ViewChild('middleResizer', { static: true }) middleResizer: ResizerComponent;
    @ViewChild('metadataGrid', { static: true }) metadataGrid: GenericGridComponent;
    @ViewChild('historyGrid', { static: true }) historyGrid: GenericGridComponent;
    @ViewChild('familyGrid', { static: true }) familyGrid: DocumentListComponent;
    @ViewChild('threadGrid', { static: true }) threadGrid: DocumentListComponent;
    @ViewChild('docListGrid', { static: true }) docListGrid: DocumentListComponent;
    @ViewChild('tagQueueGrid') tagQueueGrid: GenericGridComponent;
    @ViewChild('downloadLink', { read: ElementRef, static: true }) downloadLink: ElementRef;
    @ViewChild('documentFrame', { read: ElementRef, static: true }) documentFrame: ElementRef;

    readonly DocumentListTabType = DocumentListTabType;
    readonly DocumentFormat = DocumentFormat;
    readonly TagScope = TagScope;
    readonly currentBrowserIsIE = this.commonService.currentBrowserIsIE;

    width: number;
    selectedDocument: DrDocument;
    displayFormat: DocumentFormat = DocumentFormat.HTML5_HIGHLIGHTED;
    isExternalApp: boolean;
    private closeDocumentViewerRequestEvent: Subscription;

    @HostListener('window:resize', ['$event'])
    onResize(event) {
        this.width = window.innerWidth;
    }

    @HostListener('window:resizerResize', ['$event'])
    onResizerResize(event) {
        this.width = window.innerWidth;
    }

    // Doc Viewer Permissions
    hasPrjAliaslistPermissions = Permissions.checkUserPermissions([{
        permissionType: ObjectActionListType.PRJ_ALIAS_LISTS,
        permissionActions: [ActionType.CREATE]
    }]);

    hasPrjDomainListPermissions = Permissions.checkUserPermissions([{
        permissionType: ObjectActionListType.PRJ_DOMAIN_LISTS,
        permissionActions: [ActionType.CREATE]
    }]);

    hasProfilesPermissions = Permissions.checkUserPermissions([{
        permissionType: ObjectActionListType.PROFILES,
        permissionActions: [ActionType.CREATE]
    }]);

    showAddSelectedTextTo = this.hasPrjAliaslistPermissions || this.hasPrjDomainListPermissions || this.hasProfilesPermissions;

    hasDownloadNativePermissions = Permissions.checkUserPermission(ObjectActionListType.DOWNLOAD_NATIVES);

    hasDownloadPDFPermissions = Permissions.checkUserPermission(ObjectActionListType.DOWNLOAD_PDF);

    showDownloadButton = this.hasDownloadNativePermissions || this.hasDownloadPDFPermissions;

    hasDownloadMetadataPermissions = Permissions.checkUserPermission(ObjectActionListType.DOCUMENT_REPORTS);

    hasTagsAddEditPermission = Permissions.checkUserPermissions([{
        permissionType: ObjectActionListType.PRJ_TAGS,
        permissionActions: [ActionType.CREATE]
    }]);

    hasNewTagPermissions = Permissions.checkUserPermissions([{
        permissionType: ObjectActionListType.PRJ_TAGS,
        permissionActions: [ActionType.CREATE]
    }]);

    hasDocumentTagPermissions = Permissions.checkUserPermissions([{
        permissionType: ObjectActionListType.DOCUMENT_TAGS,
        permissionActions: [ActionType.CREATE]
    }]);

    hasAuditAccessPermissions = Permissions.checkUserPermission(ObjectActionListType.AUDIT_LOG);

    documentListTab: DocumentListTabType = DocumentListTabType.docList;
    _currentDocumentList: DocumentListComponent;
    get currentDocumentList(): DocumentListComponent {
        if (this._currentDocumentList && this._currentDocumentList.genericGrid && !this._currentDocumentList.genericGrid.isBusy &&
            this._currentDocumentList.genericGrid.gridApi) {
            return this._currentDocumentList;
        }
        return null;
    }
    set currentDocumentList(value: DocumentListComponent) {
        this._currentDocumentList = value;
    }
    get currentDocumentListApi(): GridApi {
        if (this.currentDocumentList) {
            return this._currentDocumentList.genericGrid.gridApi;
        }
        return null;
    }

    _selectedNavigationNode: GenericTreeNode;
    get selectedNavigationNode(): GenericTreeNode {
        if (!this._selectedNavigationNode) {
            this._selectedNavigationNode = this.commonService.currentClusterView ? this.commonService.currentClusterView : this.commonService.selectedNavigationNode;
        }
        return this._selectedNavigationNode;
    }

    documentListBreadcrumbs: Array<CustomSelectItem> = new Array<CustomSelectItem>();
    selectedDocumentListBreadcrumb: CustomSelectItem;

    metadataFieldDescriptions: Array<any>;
    metadataTokenizations: Array<MetadataToken>;
    metadataXml: string;
    allMetadataFields = true;
    hideEmptyMetadata = true;

    documentUrl: SafeResourceUrl;

    tagList: Array<Tag> = new Array<Tag>();
    tagQueue: Array<TagQueueItem> = new Array<TagQueueItem>();
    tagQueueContext: Array<TagQueueItem>;
    tagScope: TagScope = TagScope.MESSAGE_ATTACHMENT_GROUP;
    documentScopeDisabled: boolean;
    familyScopeDisabled: boolean;
    threadScopeDisabled: boolean;
    tagComment = '';
    documentViewerContext: DocumentViewerContext;
    externalParsingLibrary: string;

    isBusy = {
        document: false,
        metadata: false,
        history: false,
        tags: false
    };
    isBusyMessage = {
        document: 'Loading Document ...',
        metadata: 'Loading Metadata ...',
        history: 'Loading History ...',
        tags: 'Loading Tags ...'
    };
    columnDefs = {
        metadata: new Array<GenericColDef>(
            {
                field: 'key',
                sortBy: 'key',
                headerName: 'Metadata Field',
                automationId: 'metadata',
                cellTemplateType: CellTemplateType.groupIconLabel,
                maxWidth: 300,
                filterType: FilterTemplateType.freeform,
                lockVisible: true
            },
            {
                field: 'displayValue',
                headerName: 'Value',
                automationId: 'metadata',
                cellTemplateType: CellTemplateType.longReadonlyInputLabel,
                filterType: FilterTemplateType.freeform,
                lockVisible: true
            },
            {
                automationId: 'metadata',
                cellTemplateType: CellTemplateType.cellButton,
                minWidth: 30, maxWidth: 30, width: 30,
                suppressSizeToFit: true,
                suppressMovable: true
            }
        ),
        history: new Array<GenericColDef>(
            { headerName: 'Operation', field: 'operation', automationId: 'document-viewer-history', lockVisible: true },
            {
                headerName: 'Date', field: 'timestamp', automationId: 'document-viewer-history',
                cellTemplateType: CellTemplateType.date,
                headerAlignDirection: AlignDirection.right,
                width: 147,
                suppressSizeToFit: true
            },
            { headerName: 'User', field: 'user', automationId: 'document-viewer-history' },
            { headerName: 'Tag', field: 'tag', automationId: 'document-viewer-history' },
            { headerName: 'Description', field: 'description', automationId: 'document-viewer-history' }
        ),
        tagQueue: new Array<GenericColDef>(
            {
                headerName: 'Doc Number', field: 'documentNumber', automationId: 'document-viewer-tag-queue',
                width: 110,
                suppressSizeToFit: true, lockVisible: true
            },
            { headerName: 'Tagging Operations', cellTemplateType: CellTemplateType.tagQueue, automationId: 'document-viewer-tag-queue', lockVisible: true },
            {
                automationId: 'document-viewer-tag-queue',
                cellTemplateType: CellTemplateType.cellButton,
                minWidth: 30, maxWidth: 30, width: 30,
                suppressSizeToFit: true,
                suppressMovable: true
            }
        )
    };
    requestInfo = {
        history: new DrRequestInfo({
            endpoint: Endpoint.viewManager_getDocumentHistory,
            responseName: 'documentHistoryList'
        })
    };
    gridContext = {
        metadata: new GridContext({
            sortAscending: true,
            sortEnabled: false,
            uiMultiFilter: true,
            loadAndFilter: true,
            filterTreeStructure: true,
            inlineGridFilter: true
        })
    };
    loadDataFinishedCallback = {
        selectFirstItem: false,
        selectLastItem: false,

        familyList: () => {
            this.familyGrid.genericGrid.findNode((node: RowNode) => this.sameDocument(node.data as DrDocument), true);
        },
        threadList: () => {
            let item: RowNode = this.threadGrid.genericGrid.findNode((node: RowNode) => this.sameDocument(node.data as DrDocument));
            if (!item && this.selectedDocument && (this.selectedDocument.isThread || this.selectedDocument.hasThread)) {
                item = this.threadGrid.genericGrid.gridApi?.getDisplayedRowAtIndex(0) as RowNode;
            }
            if (item) {
                this.threadGrid.genericGrid.selectNode(item);
            }
        },
        docList: () => {
            let item: RowNode = null;
            if (this.loadDataFinishedCallback.selectFirstItem) {
                this.loadDataFinishedCallback.selectFirstItem = false;
                item = this.docListGrid.genericGrid.gridApi?.getDisplayedRowAtIndex(0) as RowNode;
            } else if (this.loadDataFinishedCallback.selectLastItem) {
                this.loadDataFinishedCallback.selectLastItem = false;
                item = this.docListGrid.genericGrid.gridApi?.getDisplayedRowAtIndex(this.docListGrid.genericGrid.gridApi?.getDisplayedRowCount() - 1) as RowNode;
            } else {
                item = this.docListGrid.genericGrid.findNode((node: RowNode) => this.sameDocument(node.data as DrDocument));
            }
            if (item) {
                this.docListGrid.genericGrid.selectNode(item);
            }
        }
    };
    responseCallback = {
        familyList: (response: any) => {
            if (response) {
                const groupedDocs = new Array<DrDocument>();
                if (response.attachments && response.document) {
                    let docs = [response.document].concat(response.attachments).map(d => new DrDocument(d));
                    docs = docs.sort((a, b) => (a.docnum > b.docnum) ? 1 : ((b.docnum > a.docnum) ? -1 : 0));
                    docs.forEach(document => {
                        if (document.family_handle && document.isAttachment) {
                            // family child, look for parent
                            for (let i = docs.length - 1; i >= 0; i--) {
                                if (docs[i].family_handle === document.family_handle && !docs[i].isAttachment) {
                                    document.isChild = true;
                                    document.level = 1;
                                    if (!docs[i].isParent) {
                                        docs[i].isParent = true;
                                        docs[i].children = new Array<DrDocument>();
                                        docs[i].isExpanded = true;
                                    }
                                    docs[i].children.push(document);
                                    break;
                                }
                            }
                        }
                        if (!document.isChild) {
                            groupedDocs.push(document);
                        }
                    });
                }
                response.documents = groupedDocs;
            }
        },
        threadList: (response: any) => {
            if (response) {
                const groupedMessages = new Array<EmailThreadDocument>();
                if (response.messages) {
                    const messages = (<Array<any>>response.messages).map(d => new EmailThreadDocument(d));
                    const parseMessage = (parent: EmailThreadDocument, message: EmailThreadDocument) => {
                        if (message) {
                            message.level = parent ? 1 : 0;
                            groupedMessages.push(message);
                            if (message.childDocumentList.length > 0) {
                                message.childDocumentList.forEach(child => {
                                    parseMessage(message, child);
                                });
                            }
                        }
                    };
                    messages.forEach(message => {
                        parseMessage(null, message);
                    });
                }
                response.documents = groupedMessages;
            }
        }
    };
    responseArrayCallback = {
        threadList: (value: EmailThreadDocument, fullResponse: any): Promise<EmailThreadDocument> => {
            const request = new DeferredPromise<any>();
            if (value && value.attachmentCount > 0) {
                this.restService.post(Endpoint.viewManager_getDocumentAttachments, {
                    handle: value.handle,
                    selectedAttributes: this.threadGrid.requestInfo.data.selectedAttributes
                }).then((response) => {
                    if (response && response.attachments) {
                        value.isParent = true;
                        value.isExpanded = true;
                        value.children = new Array<DrDocument>();
                        const docs = (<Array<any>>response.attachments).map(d => new DrDocument(d));
                        docs.forEach(document => {
                            document.isChild = true;
                            document.level = value.level + 1;
                            value.children.push(document);
                        });
                    }
                    request.resolve(value);
                }, error => {
                    request.resolve(value);
                });
            } else {
                request.resolve(value);
            }
            return request.promise;
        }
    };
    onSelectionChanged = {
        familyList: (event?: SelectionChangedEvent) => {
            if (event && event.api) {
                const selectedItem = event.api.getSelectedRows()[0];
                if (selectedItem instanceof DrDocument) {
                    this.setSelectedDocument(selectedItem);
                }
            }
        },
        threadList: (event?: SelectionChangedEvent) => {
            if (event && event.api) {
                const selectedItem = event.api.getSelectedRows()[0];
                if (selectedItem instanceof DrDocument) {
                    this.setSelectedDocument(selectedItem);
                }
            }
        },
        docList: (event?: SelectionChangedEvent) => {
            if (event && event.api) {
                const selectedItem = event.api.getSelectedRows()[0];
                if (selectedItem instanceof DrDocument) {
                    this.setSelectedDocument(selectedItem);
                }
            }
        }
    };
    cellClickedCallback = {
        metadata: (rowNode: RowNode, action: CellActionType) => {
            if (rowNode && rowNode.data && action && rowNode.data.key && rowNode.data.value && this.selectedNavigationNode) {
                if (action === CellActionType.cellButton) {
                    this.isBusyMessage.metadata = 'Searching ...';
                    this.isBusy.metadata = true;
                    const escapeList = [
                        'batch',
                        'batch_view',
                        'bloombergdisclaimer',
                        'bloomberguser',
                        'bloombergversion',
                        'cluster_view',
                        'contentdisposition',
                        'contentlanguage',
                        'contentransferencoding',
                        'contenttype',
                        'custodian_view',
                        'custodian',
                        'dafilestate',
                        'darelativepath',
                        'datecompleted',
                        'dateprimary',
                        'deliveredto',
                        'department',
                        'description',
                        'destination',
                        'disclaimerreference',
                        'disposition',
                        'division',
                        'documentnumber',
                        'dupe_fingerprint',
                        'entryid',
                        'exceptiontext',
                        'export_view_docid',
                        'export_view_enddoc',
                        'export_view',
                        'filename',
                        'extendedfilename',
                        'family_fingerprint',
                        'family_handle',
                        'flagstarted',
                        'flagdue',
                        'flagcompleted',
                        'fullpath',
                        'group_view',
                        'image_exception',
                        'importarea',
                        'importdevice',
                        'importpath',
                        'linksdirty',
                        'mailstop',
                        'mediaencoder',
                        'mediaid',
                        'mediaid_view',
                        'mediaformat',
                        'messageid',
                        'mimetype',
                        'mimeversion',
                        'mmclipcount',
                        'msreviewcycle',
                        'msreviewprevcycle',
                        'ocrpath',
                        'office',
                        'ordinal',
                        'ownergroup',
                        'owneruser',
                        'pattern',
                        'patternvalue',
                        'pathway',
                        'project',
                        'receivedzone',
                        'receiveheader',
                        'recordedby',
                        'reference',
                        'references',
                        'returnpath',
                        'revision',
                        'scale',
                        'smf_application',
                        'smf_generator',
                        'stored_image',
                        'source',
                        'status',
                        'tag_view',
                        'thread_handle',
                        'trackchanges',
                        'typist',
                        'drrelativepath',
                        'extdarelativepath',
                        'embeddedlink'
                    ];
                    let queryVal = rowNode.data.value as string;

                    if (escapeList.find(metadatafield => metadatafield === rowNode.data.key)) {
                        queryVal = queryVal.replace(/\s/g, ' '); // See ECASL-35397 for a description of these transformations
                        if (rowNode.data.key === 'patternvalue') {
                            queryVal = queryVal.replace(/[\\']/g, '\\$&');
                            queryVal = '\'' + queryVal + '\'';
                        } else {
                            queryVal = queryVal.replace(/([:+&|(){}[\]^"~*:?'\\])/g, '\\$&');
                            if (queryVal.match(/[\\!*$ ]/)) {
                                queryVal = '"' + queryVal + '"';
                            } else if (queryVal.length > 0 && queryVal[0] === '\'') {
                                queryVal = '\\' + queryVal;
                            }

                        }
                    }
                    const queryString = rowNode.data.key + (this.commonService.searchDialect === 'DR_CLASSIC' ? ':' : '::') + this.formatSearchTermByTokenizationType(rowNode.data.key, queryVal);
                    const xmlQuery = this.xmlService.stringToXMLQuery(queryString);
                    const selectedNodeHandle = (this.selectedNavigationNode.data instanceof DynamicFolder && this.selectedNavigationNode.data?.view?.handle) ? this.selectedNavigationNode.data.view.handle : this.selectedNavigationNode.data.handle;
                    this.restService.post(Endpoint.viewManager_search, {
                        viewHandleList: [selectedNodeHandle],
                        query: xmlQuery,
                        includeFamilyMembers: true,
                        expandFields: true,
                        includeSynonyms: false,
                        taskDescription: '(' + queryString + ') in ' + this.selectedNavigationNode.name,
                        attributes: [{
                            name: 'searchDialect',
                            value: this.commonService.searchDialect
                        }],
                        reportRequests: [],
                        corporaFilters: []
                    }).then(response => {
                        this.isBusy.metadata = false;
                        if (response && response.viewHandle && this.commonService.isExternalApp && window.opener) {
                            window.opener.navigateTo(response.viewHandle);
                        }
                    }, error => {
                        this.isBusy.metadata = false;
                    });
                }
            }
        },
        tagQueue: (rowNode: RowNode, action: CellActionType) => {
            if (rowNode && rowNode.data instanceof TagQueueItem && action === CellActionType.cellButton) {
                this.tagQueue = _.without(this.tagQueue, rowNode.data);
                this.tagQueueGrid.setData(this.tagQueue);
                this.setTags();
            }
        }
    };

    formatSearchTermByTokenizationType(metadataField: string, searchTerm: string): string {
        if (this.metadataTokenizations?.length > 0) {
            const tokenizationObject = this.metadataTokenizations.find(elem => elem.name === metadataField);
            if (tokenizationObject) {
                switch (tokenizationObject.tokenType) {
                    case MetadataTokenizationType.untokenized:
                    case MetadataTokenizationType.lowercase_untokenized:
                        return searchTerm;
                    case MetadataTokenizationType.standard:
                    case MetadataTokenizationType.standard_with_wildcard:
                        return `"${searchTerm.replace(/"/g, '')}"`;
                    case MetadataTokenizationType.path:
                        return `"${searchTerm}"`;
                }
            }
        }
        return searchTerm;
    }

    moveToDocument = {
        disabled: {
            backward: true,
            forward: true
        },
        move: (direction: string) => {
            if (this.selectedDocument && this.currentDocumentListApi) {
                const selectedNode = this.currentDocumentListApi.getSelectedNodes()[0];
                if (selectedNode) {
                    let currentPage = isNumber(this.currentDocumentList.requestInfo.data.startIndex) ?
                        (Math.ceil(this.currentDocumentList.requestInfo.data.startIndex / PagerComponent.itemsOnPage) + 1) : 1;
                    const totalPages = isNumber(this.currentDocumentList.requestInfo.data.startIndex) ?
                        Math.ceil(this.currentDocumentList.genericGrid.totalItems / PagerComponent.itemsOnPage) : 1;
                    let loadData: boolean;
                    let newIndex: number;

                    switch (direction) {
                        case 'first': {
                            if (currentPage > 1) {
                                this.loadDataFinishedCallback.selectFirstItem = true;
                                currentPage = 1;
                                loadData = true;
                            } else {
                                newIndex = 0;
                            }
                            break;
                        }
                        case 'previous': {
                            if (selectedNode.rowIndex === 0 && currentPage > 1) {
                                this.loadDataFinishedCallback.selectLastItem = true;
                                currentPage--;
                                loadData = true;
                            } else {
                                newIndex = selectedNode.rowIndex - 1;
                            }
                            break;
                        }
                        case 'next': {
                            if (selectedNode.rowIndex === this.currentDocumentListApi.getDisplayedRowCount() - 1 && currentPage < totalPages) {
                                this.loadDataFinishedCallback.selectFirstItem = true;
                                currentPage++;
                                loadData = true;
                            } else {
                                newIndex = selectedNode.rowIndex + 1;
                            }
                            break;
                        }
                        case 'last': {
                            if (currentPage < totalPages) {
                                this.loadDataFinishedCallback.selectLastItem = true;
                                currentPage = totalPages;
                                loadData = true;
                            } else {
                                newIndex = this.currentDocumentListApi.getDisplayedRowCount() - 1;
                            }
                            break;
                        }
                    }
                    if (loadData) {
                        this.currentDocumentList.genericGrid.loadData((currentPage - 1) * PagerComponent.itemsOnPage);
                    } else if (isNumber(newIndex)) {
                        const item = this.currentDocumentListApi.getDisplayedRowAtIndex(newIndex);
                        if (item) {
                            this.currentDocumentList.genericGrid.selectNode(item);
                        }
                    }
                }
            }
        },
        set: () => {
            this.moveToDocument.disabled.backward = true;
            this.moveToDocument.disabled.forward = true;

            if (this.selectedDocument && this.currentDocumentListApi) {
                const selectedNode = this.currentDocumentListApi.getSelectedNodes()[0];
                if (selectedNode) {
                    if (selectedNode.rowIndex > 0 || this.currentDocumentList.requestInfo.data.startIndex > 0) {
                        this.moveToDocument.disabled.backward = false;
                    }
                    if (selectedNode.rowIndex < this.currentDocumentListApi.getDisplayedRowCount() - 1 ||
                        Math.ceil(this.currentDocumentList.requestInfo.data.startIndex / PagerComponent.itemsOnPage) + 1 <
                        Math.ceil(this.currentDocumentList.genericGrid.totalItems / PagerComponent.itemsOnPage)) {
                        this.moveToDocument.disabled.forward = false;
                    }
                }
            }
        }
    };

    documentTools = {
        OIT: null,
        OITLoaded: false,
        onOITLoaded: (event) => {
            this.documentTools.OIT.document.removeEventListener('load', this.documentTools.onOITLoaded);
            this.documentTools.OITLoaded = true;
            if (this.documentViewerContext) {
                if (this.documentViewerContext.findInDocument) {
                    this.documentTools.findInDocument.value = this.documentViewerContext.findInDocument;
                    this.documentTools.findInDocument.searchAfterBounce();
                }
                if (isNumber(this.documentViewerContext.zoom)) {
                    this.documentTools.zoom.currentZoom = this.documentViewerContext.zoom;
                    this.documentTools.OIT.document.zoom(this.documentTools.zoom.currentZoom);
                }
                if (isNumber(this.documentViewerContext.rotation)) {
                    this.documentTools.rotate.currentRotation = this.documentTools.OIT.document.rotate(this.documentViewerContext.rotation);
                }
                this.documentViewerContext = null;
            } else if (this.documentTools.findInDocument.value) {
                this.documentTools.findInDocument.search();
            }
            this.changeDetector.detectChanges();
        },
        Hyland: null,
        HylandLoaded: false,
        onHylandLoaded: () => {
            this.documentTools.HylandLoaded = true;
            if (this.documentViewerContext) {
                if (this.documentViewerContext.findInDocument) {
                    this.documentTools.findInDocument.value = this.documentViewerContext.findInDocument;
                    this.documentTools.findInDocument.searchAfterBounce();
                }
                if (isNumber(this.documentViewerContext.zoom)) {
                    this.documentTools.zoom.currentZoom = this.documentViewerContext.zoom;
                    this.documentTools.Hyland.SetZoom(this.documentTools.zoom.currentZoom);
                }
                if (isNumber(this.documentViewerContext.rotation) && this.documentTools.Hyland) {
                    this.documentTools.rotate.currentRotation = this.documentTools.Hyland.rotate(this.documentViewerContext.rotation);
                }
                this.documentViewerContext = null;
            } else if (this.documentTools.findInDocument.value) {
                this.documentTools.findInDocument.search();
            }
        },
        valid: () => this.docToolsValid(),
        reset: () => {
            if (!this.commonService.isLocalHost && this.documentFrame.nativeElement.contentWindow && this.documentFrame.nativeElement.contentWindow.document) {
                this.leftResizer.alternativeMouseEventElement = null;
                this.documentFrame.nativeElement.contentWindow.document.removeEventListener('mouseup', this.documentTools.addSelectedText.checkSelection);
                this.documentFrame.nativeElement.contentWindow.document.removeEventListener('copy', this.documentTools.onClipboardCopy);
            }
            this.documentTools.OIT = null;
            this.documentTools.OITLoaded = false;
            this.documentTools.Hyland = null;
            this.documentTools.HylandLoaded = false;
            this.documentTools.addSelectedText.selectedText = '';
            this.documentTools.findInDocument.clear(false);
            this.documentTools.zoom.currentZoom = 1;
        },
        set: () => {
            if (!this.commonService.isLocalHost && this.documentFrame.nativeElement.contentWindow && this.documentFrame.nativeElement.contentWindow.document) {
                this.documentTools.OIT = this.documentFrame.nativeElement.contentWindow.OIT;
                this.documentTools.Hyland = this.documentFrame.nativeElement.contentWindow.documentViewer;
                this.leftResizer.alternativeMouseEventElement = this.documentFrame.nativeElement.contentWindow.document;
                this.documentFrame.nativeElement.contentWindow.document.addEventListener('mouseup', this.documentTools.addSelectedText.checkSelection, { passive: true });
                this.documentFrame.nativeElement.contentWindow.document.addEventListener('copy', this.documentTools.onClipboardCopy, { passive: true });
            }
            if (this.commonService.externalParsingLibrary === 'OIT') {
                if (this.documentTools.OIT && this.documentTools.OIT.document) {
                    this.documentTools.OIT.document.addEventListener('load', this.documentTools.onOITLoaded, { passive: true });
                } else if (this.documentFrame.nativeElement.contentWindow.OIT) {
                    this.documentTools.OIT = this.documentFrame.nativeElement.contentWindow.OIT;
                }
            } else {
                if (this.documentTools.Hyland && this.documentFrame.nativeElement.contentWindow.document) {
                    this.documentTools.onHylandLoaded();
                } else if (this.documentFrame.nativeElement.contentWindow.documentViewer) {
                    this.documentTools.Hyland = this.documentFrame.nativeElement.contentWindow.documentViewer;
                }

                const highlightOverlays = this.documentFrame.nativeElement.contentWindow.document.getElementsByClassName("idf-page-overlay");
                if (highlightOverlays.length > 0) {
                    for (let i = 0; i < highlightOverlays.length; i++) {
                        if (highlightOverlays[i]) {
                            highlightOverlays[i].style.display = this.documentTools.highlightSearchTerms.value ? "" : "none";
                        }
                    }
                }

                const detailsPanel = this.documentFrame.nativeElement.contentWindow.document.getElementsByClassName("perceptive-viewer-details");
                if (detailsPanel && detailsPanel[0]) {
                    detailsPanel[0].style.display = 'none';
                }
                this.documentTools.findInDocument.isBusy = false;
            }
        },
        hiddenContentTooltip: 'Hidden content in this document: Document Annotations',
        documentInfo: {
            value: '',
            set: () => {
                let index = 1;
                let count = 1;
                if (this.currentDocumentListApi) {
                    const row = this.currentDocumentListApi.getSelectedNodes()[0];
                    if (row) {
                        index = row.rowIndex + 1 + (isNumber(this.docListGrid.requestInfo.data.startIndex) ?
                            this.docListGrid.requestInfo.data.startIndex : 0);
                        count = this.currentDocumentList.genericGrid.totalItems > this.currentDocumentList.genericGrid.getData().length ?
                            this.docListGrid.genericGrid.totalItems : this.currentDocumentList.genericGrid.getData().length;
                        this.documentTools.documentInfo.value = `${this.selectedDocument.docnum} ( ${index} / ${count} )`;
                    }
                }
                this.documentTools.documentInfo.value = `${this.selectedDocument.docnum} ( ${index} / ${count} )`;
                const showTooltip = this.selectedDocument.hiddendata || this.selectedDocument.trackchanges || this.selectedDocument.docannotations;
                let contentTooltip = showTooltip ? 'This document has document annotations, hidden data, and/or Word Track Changes: \n' : '';
                if (showTooltip) {
                    if (this.selectedDocument.docannotations) {
                        contentTooltip += `docannotations field has ${this.selectedDocument.docannotations.replace(new RegExp(' ;', 'g'), ';')} \n`;
                    }
                    if (this.selectedDocument.hiddendata) {
                        contentTooltip += `hiddendata field has ${this.selectedDocument.hiddendata.replace(new RegExp(' ;', 'g'), ';')} \n`
                    }
                    if (this.selectedDocument.trackchanges) {
                        contentTooltip += 'trackchanges field has true';
                    }
                }

                this.documentTools.hiddenContentTooltip = contentTooltip;
            }
        },
        highlightSearchTerms: {
            visible: false,
            value: true,
            changed: () => {
                if (this.documentTools.valid()) {
                    if (this.commonService.externalParsingLibrary === 'OIT') {
                        this.documentTools.highlightSearchTerms.value ? this.documentTools.OIT.highlights.show() : this.documentTools.OIT.highlights.hide();
                    } else {
                        const highlightOverlays = this.documentFrame.nativeElement.contentWindow.document.getElementsByClassName("idf-page-overlay");
                        if (highlightOverlays.length > 0) {
                            for (let i = 0; i < highlightOverlays.length; i++) {
                                if (highlightOverlays[i]) {
                                    highlightOverlays[i].style.display = this.documentTools.highlightSearchTerms.value ? "" : "none";
                                }
                            }
                        }
                    }
                }
            }
        },
        zoom: {
            currentZoom: 1,
            fitWidthZoom: 0,
            fitHeightZoom: 0,
            resetEnabled: () => this.documentTools?.valid() && this.documentTools.zoom.currentZoom !== (this.commonService.externalParsingLibrary === 'OIT' ? 1 : 1.0),
            reset: () => {
                if (this.documentTools?.valid()) {
                    if (this.commonService.externalParsingLibrary === 'OIT') {
                        this.documentTools.zoom.currentZoom = this.documentTools.OIT.document.zoom(1);
                    } else {
                        this.documentTools.Hyland.zoomReset();
                        this.documentTools.zoom.currentZoom = parseFloat(this.documentTools.Hyland._scale.toFixed(1));
                    }
                }
            },
            outEnabled: () => this.documentTools?.valid() && this.documentTools.zoom.currentZoom > 0.1,
            out: () => {
                if (this.documentTools?.valid()) {
                    if (this.commonService.externalParsingLibrary === 'OIT') {
                        this.documentTools.zoom.currentZoom = this.documentTools.OIT.document.zoomOut();
                    } else {
                        this.documentTools.Hyland.zoomOut();
                        this.documentTools.zoom.currentZoom = parseFloat(this.documentTools.Hyland._scale.toFixed(1));
                    }
                }
            },
            inEnabled: () => this.documentTools?.valid() && this.documentTools.zoom.currentZoom < (this.commonService.externalParsingLibrary === 'OIT' ? 40 : 2.5),
            in: () => {
                if (this.documentTools?.valid()) {
                    if (this.commonService.externalParsingLibrary === 'OIT') {
                        this.documentTools.zoom.currentZoom = this.documentTools.OIT.document.zoomIn();
                    } else {
                        this.documentTools.Hyland.zoomIn();
                        this.documentTools.zoom.currentZoom = parseFloat(this.documentTools.Hyland._scale.toFixed(1));
                    }
                }
            },
            fitWidthEnabled: () => this.documentTools?.valid() && this.documentTools.zoom.currentZoom !== this.documentTools.zoom.fitWidthZoom,
            fitWidth: () => {
                if (this.documentTools?.valid()) {
                    if (this.commonService.externalParsingLibrary === 'OIT') {
                        this.documentTools.zoom.currentZoom = this.documentTools.zoom.fitWidthZoom = this.documentTools.OIT.document.zoom('pagewidth');
                    } else {
                        let frameScrollWidth = this.documentFrame.nativeElement.contentWindow.document.documentElement.scrollWidth;
                        let pageWidth = this.documentFrame.nativeElement.contentWindow.document.getElementById('Page1').getBoundingClientRect().width;

                        while (frameScrollWidth > Math.round(pageWidth) && this.documentTools.zoom.currentZoom !== 2.5) {
                            this.documentTools.Hyland.zoomIn();
                            this.documentTools.zoom.currentZoom = this.documentTools.zoom.fitWidthZoom = parseFloat(this.documentTools.Hyland._scale.toFixed(1));
                            frameScrollWidth = this.documentFrame.nativeElement.contentWindow.document.documentElement.scrollWidth;
                            pageWidth = this.documentFrame.nativeElement.contentWindow.document.getElementById('Page1').getBoundingClientRect().width;
                        }

                        while (frameScrollWidth < Math.round(pageWidth) && this.documentTools.zoom.currentZoom !== 0.3) {
                            this.documentTools.Hyland.zoomOut();
                            this.documentTools.zoom.currentZoom = this.documentTools.zoom.fitWidthZoom = parseFloat(this.documentTools.Hyland._scale.toFixed(1));
                            frameScrollWidth = this.documentFrame.nativeElement.contentWindow.document.documentElement.scrollWidth;
                            pageWidth = this.documentFrame.nativeElement.contentWindow.document.getElementById('Page1').getBoundingClientRect().width;
                        }
                    }
                }
            },
            fitHeightEnabled: () => this.documentTools?.valid() && this.documentTools.zoom.currentZoom !== this.documentTools.zoom.fitHeightZoom,
            fitHeight: () => {
                if (this.documentTools?.valid()) {
                    if (this.commonService.externalParsingLibrary === 'OIT') {
                        this.documentTools.zoom.currentZoom = this.documentTools.zoom.fitHeightZoom = this.documentTools.OIT.document.zoom('pageheight');
                    } else {
                        let frameClientHeight = this.documentFrame.nativeElement.contentWindow.document.documentElement.clientHeight;
                        let pageHeight = this.documentFrame.nativeElement.contentWindow.document.getElementById('Page1').getBoundingClientRect().height;

                        while (frameClientHeight > Math.round(pageHeight) && this.documentTools.zoom.currentZoom !== 2.5) {
                            this.documentTools.Hyland.zoomIn();
                            this.documentTools.zoom.currentZoom = this.documentTools.zoom.fitHeightZoom = parseFloat(this.documentTools.Hyland._scale.toFixed(1));
                            frameClientHeight = this.documentFrame.nativeElement.contentWindow.document.documentElement.clientHeight;
                            pageHeight = this.documentFrame.nativeElement.contentWindow.document.getElementById('Page1').getBoundingClientRect().height;
                            if (frameClientHeight < Math.round(pageHeight)) {
                                this.documentTools.Hyland.zoomOut();
                                break;
                            }
                        }

                        while (frameClientHeight < Math.round(pageHeight) && this.documentTools.zoom.currentZoom !== 0.3) {
                            this.documentTools.Hyland.zoomOut();
                            this.documentTools.zoom.currentZoom = this.documentTools.zoom.fitHeightZoom = parseFloat(this.documentTools.Hyland._scale.toFixed(1));
                            frameClientHeight = this.documentFrame.nativeElement.contentWindow.document.documentElement.clientHeight;
                            pageHeight = this.documentFrame.nativeElement.contentWindow.document.getElementById('Page1').getBoundingClientRect().height;
                        }
                    }
                }
            }
        },
        rotate: {
            currentRotation: 0,
            left: () => {
                if (this.documentTools?.valid()) {
                    if (this.commonService.externalParsingLibrary === 'OIT') {
                        this.documentTools.rotate.currentRotation = this.documentTools.OIT.document.rotate(this.documentTools.rotate.currentRotation + 90);
                    } else {
                        this.documentTools.Hyland.rotateLeft();
                        this.documentTools.rotate.currentRotation = this.documentTools.Hyland._rotation;
                    }
                }
            },
            right: () => {
                if (this.documentTools?.valid()) {
                    if (this.commonService.externalParsingLibrary === 'OIT') {
                        this.documentTools.rotate.currentRotation = this.documentTools.OIT.document.rotate(this.documentTools.rotate.currentRotation - 90);
                    } else {
                        this.documentTools.Hyland.rotateRight();
                        this.documentTools.rotate.currentRotation = this.documentTools.Hyland._rotation;
                    }
                }
            }
        },
        addSelectedText: {
            selectedText: '',
            checkSelection: (event) => {
                this.documentTools.addSelectedText.selectedText = this.documentTools.addSelectedText.getSelectedText().trim();
                this.changeDetector.detectChanges();
            },
            getSelectedText: (): string => {
                if (this.documentTools.OIT) {
                    return this.documentTools.OIT.document.getSelectedText() || '';
                }
                const win = this.documentFrame.nativeElement.contentWindow;
                if (win.getSelection) {
                    var selection = win.getSelection();
                    if (selection?.rangeCount > 0) {
                        const contents = selection.getRangeAt(0)?.cloneContents();
                        if (contents?.children?.length > 0) {
                            let selectedText = '';
                            for (var i = 0; i < contents.children.length; i++) {
                                const currentSegment = contents.children[i];
                                selectedText += contents.children[i].innerText.replace(new RegExp('\n', 'g'), '')
                                if (i < contents.children.length - 1) {
                                    const topAttribute = currentSegment.attributes?.style?.nodeValue?.split('; ')?.find(o => o.indexOf('top: ') > -1);
                                    const topAttributeNextNode = contents.children[i + 1]?.attributes?.style?.nodeValue?.split('; ')?.find(o => o.indexOf('top: ') > -1);
                                    if (topAttribute && topAttributeNextNode && topAttribute === topAttributeNextNode) {
                                        selectedText += ' ';
                                    } else {
                                        selectedText += '\n';
                                    }
                                }
                            }
                            return selectedText;
                        } else {
                            return contents.textContent;
                        }
                    }
                    else {
                        return selection.toString();
                    }
                } else {
                    const doc = this.documentFrame.nativeElement.contentDocument || win.document;
                    if (doc && doc.selection && doc.selection.createRange) {
                        return doc.selection.createRange().text;
                    }
                }
                return '';
            },
            aliasList: () => {
                if (this.documentTools.addSelectedText.selectedText) {
                    this.commonService.openModal(AddSelectedTextToModalComponent, {
                        modalData: <AddSelectedTextToModalData>{
                            type: AddSelectedTextToModalType.AliasList,
                            selectedText: this.documentTools.addSelectedText.selectedText
                        },
                        class: 'vertical-percent-100 horizontal-percent-100'
                    });
                }
            },
            domainList: () => {
                if (this.documentTools.addSelectedText.selectedText) {
                    this.commonService.openModal(AddSelectedTextToModalComponent, {
                        modalData: <AddSelectedTextToModalData>{
                            type: AddSelectedTextToModalType.DomainList,
                            selectedText: this.documentTools.addSelectedText.selectedText
                        },
                        class: 'vertical-percent-100 horizontal-percent-100'
                    });
                }
            },
            profile: () => {
                if (this.documentTools.addSelectedText.selectedText) {
                    this.commonService.openModal(AddSelectedTextToModalComponent, {
                        modalData: <AddSelectedTextToModalData>{
                            type: AddSelectedTextToModalType.Profile,
                            selectedText: this.documentTools.addSelectedText.selectedText
                        },
                        class: 'vertical-percent-100 horizontal-percent-100'
                    });
                }
            }
        },
        download: {
            enabled: () => this.selectedDocument,
            pdfEnabled: () => this.selectedDocument,
            native: () => {
                this.restService.post(Endpoint.documentManager_retrieveDocument, {
                    documentHandle: this.selectedDocument.handle,
                    format: DocumentFormat.NATIVE,
                    enablePersistentHighlighting: false
                }).then(response => {
                    if (response?.url) {
                        this.downloadLink.nativeElement.href = this.commonService.nonRestUrl + '/ediscovery/' + response.url;
                        this.downloadLink.nativeElement.click();
                    }
                });
            },
            pdf: () => {
                this.restService.post(Endpoint.documentManager_retrieveDocument, {
                    documentHandle: this.selectedDocument.handle,
                    format: DocumentFormat.PDF,
                    enablePersistentHighlighting: false
                }).then(response => {
                    if (response?.url) {
                        let url = this.commonService.nonRestUrl + '/ediscovery/' + response.url;
                        if (!url.includes('&download=true')) {
                            url += '&download=true';
                        }
                        this.downloadLink.nativeElement.href = url;
                        this.downloadLink.nativeElement.click();
                    }
                });
            }
        },
        findInDocument: {
            enabled: () => this.displayFormat === DocumentFormat.TEXT ? true : (this.documentTools.valid() &&
                (this.commonService.externalParsingLibrary === 'OIT' ? this.documentTools.OIT?.highlights : this.documentTools.Hyland)),
            value: '',
            resultIndex: -1,
            results: null,
            searchFilter: (id, props) => props ? props.type === 'searchResult' : false,
            isBusy: false,
            debounceSearchFilter: null,
            search: () => {
                if (!this.documentTools.findInDocument.debounceSearchFilter) {
                    this.documentTools.findInDocument.debounceSearchFilter = _.debounce(this.documentTools.findInDocument.searchAfterBounce, 1000);
                }
                this.documentTools.findInDocument.debounceSearchFilter();
            },
            searchAfterBounce: () => {
                this.documentTools.findInDocument.isBusy = false;
                this.documentTools.findInDocument.value = this.documentTools.findInDocument.value.trim();
                if (this.displayFormat === DocumentFormat.TEXT) {
                    this.documentTools.findInDocument.clear(false);

                    if (this.documentTools.findInDocument.value !== '') {
                        this.documentTools.findInDocument.resultIndex = -1;
                        this.highlightText(this.documentTools.findInDocument.value);
                    } else {
                        this.documentTools.findInDocument.value = '';
                    }

                } else if (this.documentTools.valid() && !this.documentTools.findInDocument.isBusy) {
                    this.documentTools.findInDocument.clear(false);

                    if (isString(this.documentTools.findInDocument.value) && this.documentTools.findInDocument.value.length >= 2) {
                        this.documentTools.findInDocument.isBusy = true;
                        const searchValue = this.documentTools.findInDocument.value;

                        if (this.commonService.externalParsingLibrary === 'OIT') {
                            const results = [];
                            const docTools = this.documentTools;
                            this.documentTools.OIT.document.findAll(this.documentTools.findInDocument.value, (textRange) => {
                                if (textRange) {
                                    const id = this.documentTools.OIT.highlights.add('background-color: #D2FF00', textRange, { type: 'searchResult' });
                                    results.push(id);
                                    docTools.OIT.highlights.setOptions(id,
                                        { enableUserDelete: false, enableUserMove: false, enableUserComment: false, enableUserResize: false });
                                    this.documentTools.OIT.highlights.addEventListener('activate', function (evt) {
                                        if (evt && evt.detail && !isUndefined(evt.detail.highlightid)) {
                                            docTools.OIT.highlights.setOptions(evt.detail.highlightid,
                                                { enableUserDelete: false, enableUserMove: false, enableUserComment: false, enableUserResize: false });
                                        }
                                        docTools.OIT.highlights.removeEventListener('activate');
                                    }, { passive: true });
                                } else {
                                    if (this.documentTools.findInDocument.isBusy) {
                                        this.documentTools.findInDocument.results = results;
                                        this.documentTools.findInDocument.isBusy = false;
                                        if (searchValue !== this.documentTools.findInDocument.value) {
                                            this.documentTools.findInDocument.searchAfterBounce();
                                        } else if (this.documentTools.findInDocument.results.length > 0) {
                                            this.documentTools.findInDocument.next();
                                        }
                                        this.changeDetector.detectChanges();
                                    }
                                }
                            }, false, { type: 'searchResult', enableUserMove: false, enableUserDelete: false, enableUserComment: false });

                        } else {
                            this.highlightText(searchValue);
                        }
                    }
                }
            },
            previous: () => {
                if (this.displayFormat === DocumentFormat.TEXT) {
                    this.selectFindInDocumentTextTab(-1);

                } else if (this.documentTools.valid && this.documentTools.findInDocument.results) {
                    if (this.commonService.externalParsingLibrary === 'OIT') {
                        this.documentTools.findInDocument.resultIndex--;
                        if (this.documentTools.findInDocument.resultIndex < 0) {
                            this.documentTools.findInDocument.resultIndex = this.documentTools.findInDocument.results.length - 1;
                        }
                        this.documentTools.OIT.highlights.moveto(this.documentTools.findInDocument.results[this.documentTools.findInDocument.resultIndex]);
                    } else {
                        if (this.documentTools.findInDocument.resultIndex < 0) {
                            this.documentTools.findInDocument.resultIndex = this.documentTools.findInDocument.results.length - 1;
                        }
                        this.selectFindInDocumentTextTab(-1);
                    }
                }
            },
            next: () => {
                if (this.displayFormat === DocumentFormat.TEXT) {
                    this.selectFindInDocumentTextTab(1);
                } else if (this.documentTools.valid && this.documentTools.findInDocument.results) {
                    if (this.commonService.externalParsingLibrary === 'OIT') {
                        this.documentTools.findInDocument.resultIndex++;
                        if (this.documentTools.findInDocument.resultIndex >= this.documentTools.findInDocument.results.length) {
                            this.documentTools.findInDocument.resultIndex = 0;
                        }
                        this.documentTools.OIT.highlights.moveto(this.documentTools.findInDocument.results[this.documentTools.findInDocument.resultIndex]);
                    } else {
                        if (this.documentTools.findInDocument.resultIndex >= this.documentTools.findInDocument.results) {
                            this.documentTools.findInDocument.resultIndex = 0;
                        }
                        this.selectFindInDocumentTextTab(1);
                    }
                }
            },
            clear: (clearValue = true) => {
                if (clearValue) {
                    this.documentTools.findInDocument.value = '';
                }
                this.documentTools.findInDocument.resultIndex = -1;
                this.documentTools.findInDocument.results = null;
                if (this.documentTools.valid() && this.documentTools?.OIT?.highlights) {
                    this.documentTools.OIT.highlights.clear(this.documentTools.findInDocument.searchFilter);
                }
                if (this.commonService.externalParsingLibrary !== 'OIT') {
                    this.highlightText(this.documentTools.findInDocument.value, true);
                }

                if (this.displayFormat === DocumentFormat.TEXT) {
                    this.highlightText(this.documentTools.findInDocument.value, true);
                }
            },
        },
        onClipboardCopy: (event) => {
            event.clipboardData.setData('text/plain', this.documentTools.addSelectedText.selectedText);
            event.preventDefault();
            this.changeDetector.detectChanges();
        }
    };

    highlightText(searchValue: string, clearHighlights?: boolean): void {
        const markInstance = new Mark(this.documentFrame.nativeElement.contentWindow.document);

        const options = {
            "separateWordSearch": false,
            "acrossElements": true,
            "ignoreJoiners": true,
            "iframes": true,
            "iframesTimeout": "100",

            "done": (counter) => {
                this.documentTools.findInDocument.results = counter;
            }
        };

        if (this.documentTools.findInDocument.value === '' || clearHighlights || this.documentTools.findInDocument.value.length < 2) {
            markInstance.unmark(options);
            return;
        } else {
            markInstance.unmark({
                done: () => {
                    markInstance.mark((this.documentTools.findInDocument.value || ''), options);
                }
            });
        }

        this.documentTools.findInDocument.isBusy = false;
        this.changeDetector.detectChanges();

        if (!clearHighlights) {
            if (searchValue !== this.documentTools.findInDocument.value) {
                this.documentTools.findInDocument.searchAfterBounce();
            } else if (this.documentTools.findInDocument.results > 0) {
                this.documentTools.findInDocument.next();
            }
        }

    }

    docToolsValid(): boolean {
        if (this.commonService.externalParsingLibrary === 'OIT') {
            return this.documentTools.OIT && this.documentTools.OIT.document && this.selectedDocument && this.documentTools.OITLoaded;
        } else {
            return this.documentTools.Hyland && this.documentFrame.nativeElement.contentWindow.document && this.selectedDocument && this.documentTools.HylandLoaded;
        }
    }

    @HostListener('window:beforeunload', ['$event'])
    beforeUnloadHandler($event: any) {
        if (this.commonService.isExternalApp) {
            if (this.tagQueue && this.tagQueue.length > 0) {
                return false;
            }
            return;
        }
        if (localStorage.getItem(LocalStorageKey.AppForcedReload)) {
            // prevent showing browsers 'Reload site' popup when current user is logged out from another login session
            return;
        } else {
            return false;
        }
    }

    constructor(private commonService: CommonService, private restService: RestService, private router: Router, private xmlService: XMLService,
        private sanitizer: DomSanitizer, private changeDetector: ChangeDetectorRef, private httpService: HttpService, private metadataService: MetadataService) {
        this.closeDocumentViewerRequestEvent = this.commonService.getMessage(MessageType.closeDocumentViewer).subscribe((data) => {
            this.onCloseDocumentViewer(true);
        });
    }

    ngAfterContentChecked() {
        this.changeDetector.detectChanges();
    }

    ngOnInit(): void {
        this.externalParsingLibrary = this.commonService.externalParsingLibrary;
        this.isExternalApp = this.commonService.isExternalApp;
        this.familyGrid.isDocumentViewerFamilyList = true;
        this.threadGrid.isDocumentViewerThreadList = true;
        this.docListGrid.isDocumentViewerDocList = true;
        this.commonService.isTagTaskInProgress = false;
        this.familyGrid.openDocumentViewer = this.threadGrid.openDocumentViewer = this.docListGrid.openDocumentViewer = (item: DrDocument, type: DocumentListTabType, external: boolean) => {
            if (item && type) {
                if (external) {
                    this.spawnWindow(item, type);
                } else {
                    this.setSelectedDocument(item, true);
                    if (this.documentListTab !== type) {
                        this.setDocumentListTab(type, false);
                    }
                    if (this.currentDocumentList) {
                        this.currentDocumentList.genericGrid.findNode((node: RowNode) => this.sameDocument(node.data as DrDocument), true);
                    }
                }
            }
        };

        if (this.selectedNavigationNode) {
            this.docListGrid.selectedTreeItemChanged(this.selectedNavigationNode);
            this.threadGrid.selectedTreeItemChanged(this.selectedNavigationNode, true);
            if (this.commonService.documentViewerOpenContext) {
                this.documentViewerContext = this.commonService.documentViewerOpenContext;
                if (this.documentViewerContext) {
                    if (this.documentViewerContext.displayFormat) {
                        this.displayFormat = this.documentViewerContext.displayFormat;
                    }
                    if (isBoolean(this.documentViewerContext.allMetadataFields)) {
                        this.allMetadataFields = this.documentViewerContext.allMetadataFields;
                    }
                    if (isBoolean(this.documentViewerContext.hideEmptyMetadata)) {
                        this.hideEmptyMetadata = this.documentViewerContext.hideEmptyMetadata;
                    }
                    if (isString(this.documentViewerContext.tagComment)) {
                        this.tagComment = this.documentViewerContext.tagComment;
                    }
                    if (this.documentViewerContext.tagScope) {
                        this.tagScope = this.documentViewerContext.tagScope;
                    }
                    if (isArray(this.documentViewerContext.tagQueue)) {
                        this.tagQueueContext = this.documentViewerContext.tagQueue.map(tq => new TagQueueItem(tq));
                    }
                    if (this.documentViewerContext.selectedDocument) {
                        this.setSelectedDocument(this.documentViewerContext.selectedDocument);
                    }
                    if (this.documentViewerContext.documentListTab) {
                        this.setDocumentListTab(this.documentViewerContext.documentListTab);
                    }
                    if (this.documentViewerContext.isShortMessageThreads) {
                        if (this.threadGrid) {
                            this.threadGrid.isShortMessageThreads = this.documentViewerContext.isShortMessageThreads;
                        }
                        if (this.docListGrid) {
                            this.docListGrid.isShortMessageThreads = this.documentViewerContext.isShortMessageThreads;
                        }
                    }
                }
            }
            this.loadTags();

            if (this.selectedNavigationNode.data) {
                this.documentScopeDisabled = this.scopeDisabled(TagScope.DOCUMENT_ONLY);
                this.familyScopeDisabled = this.scopeDisabled(TagScope.MESSAGE_ATTACHMENT_GROUP);
                this.threadScopeDisabled = this.scopeDisabled(TagScope.EMAIL_THREAD);
            }
        }
    }

    scopeDisabled(scope: TagScope): boolean {
        const selectedTabPath = this.commonService?.selectedNavigationNode?.navigationInfo?.selectedTab?.path;
        const isThreadTab = selectedTabPath === 'email-thread' || selectedTabPath === 'short-message-thread';
        if (this.selectedNavigationNode.entityType === EntityType.import || this.selectedNavigationNode.data.viewType === ViewType.CORPUS_RESULT ||
            this.selectedNavigationNode.data.viewType === ViewType.CORPUS_SET_RESULT) {
            this.tagScope = TagScope.DOCUMENT_ONLY;
            return scope !== TagScope.DOCUMENT_ONLY;
        } else if (isThreadTab) {
            this.tagScope = TagScope.EMAIL_THREAD;
            return scope !== TagScope.EMAIL_THREAD;
        }
        return false;
    }

    setSelectedDocument(doc: DrDocument, skipDocLoad?: boolean) {
        if (doc) {
            const sameDoc = this.sameDocument(doc);
            this.selectedDocument = doc;
            if (!sameDoc) {
                this.loadData();
                this.loadDocument();
                this.setTags();
            }
            this.moveToDocument.set();
            this.documentTools.documentInfo.set();
        }
    }

    sameDocument(doc: DrDocument) {
        const handle1 = doc ? doc.handle.substring(doc.handle.indexOf('@') + 1) : 'handle1';
        const handle2 = this.selectedDocument ? this.selectedDocument.handle.substring(this.selectedDocument.handle.indexOf('@') + 1) : 'handle2';
        return handle1 === handle2;
    }

    loadDocument() {
        if (this.selectedDocument) {
            const currentFormat = this.displayFormat;
            this.documentTools.reset();
            this.documentUrl = this.sanitizer.bypassSecurityTrustResourceUrl('about:blank');
            if (this.displayFormat === DocumentFormat.IMAGE_PDF && !this.selectedDocument.stored_image) {
                this.setDisplayFormat(DocumentFormat.HTML5_HIGHLIGHTED);
                return;
            }
            this.isBusy.document = true;
            if (this.selectedDocument.isThread) {
                return;
            }
            this.documentTools.highlightSearchTerms.visible = this.displayFormat === DocumentFormat.HTML5_HIGHLIGHTED &&
                this.selectedNavigationNode.entityType === EntityType.searchResult;
            this.restService.post(Endpoint.documentManager_retrieveDocument, {
                documentHandle: this.selectedDocument.handle,
                format: this.displayFormat,
                enablePersistentHighlighting: false
            }, false).then(response => {
                if (this.commonService.isLocalHost || this.displayFormat === DocumentFormat.IMAGE_PDF || currentFormat !== this.displayFormat) {
                    this.isBusy.document = false;
                }
                if (currentFormat === this.displayFormat) {
                    if (response?.url) {
                        this.documentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.commonService.nonRestUrl + '/ediscovery/' + response.url);
                    } else {
                        this.isBusy.document = false;
                        this.showContentMessage('Document not found', true);
                    }
                }
            }, error => {
                this.isBusy.document = false;
                switch (error.errorCode) {
                    case 'DOCUMENT_NOT_FOUND':
                        this.showContentMessage('Error: ' + error.errorCode + ' Details: ' + error.extendedStatus);
                        break;
                    case 'CAE_ERROR':
                        this.showHylandErrorMessage(error.extendedStatus);
                        break;
                    default:
                        this.showContentMessage(this.commonService.getErrorMessage(error));
                        break;
                }
            });
        }
    }

    onDocumentLoaded(): void {
        if (this.displayFormat === DocumentFormat.IMAGE_PDF && !this.selectedDocument.stored_image) {
            this.showContentMessage('No Image Available', true, '#666');
            this.documentTools.set();
            return;
        }
        if (!this.commonService.isLocalHost) {
            // cannot access iframe document while cross-domain
            if (this.documentFrame.nativeElement.contentWindow.document && this.documentFrame.nativeElement.contentWindow.document.body &&
                this.documentFrame.nativeElement.contentWindow.document.URL !== 'about:blank') {
                this.isBusy.document = false;
            }
        }
        this.documentTools.set();
        if (this.displayFormat === DocumentFormat.TEXT) {
            if (this.documentViewerContext?.findInDocument) {
                this.documentTools.findInDocument.value = this.documentViewerContext.findInDocument;
                this.documentTools.findInDocument.search();
            } else if (this.documentTools.findInDocument.value) {
                this.documentTools.findInDocument.search();
            }
        }
    }

    showContentMessage(message: string, center = false, background?: string) {
        const doc: Document = this.documentFrame.nativeElement.contentWindow ? this.documentFrame.nativeElement.contentWindow.document : null;
        if (doc) {
            while (doc.body.firstChild) {
                doc.body.removeChild(doc.body.firstChild);
            }
            const span = doc.createElement('span');
            span.innerText = message;
            span.style.font = `400 13px 'Roboto', sans-serif`;
            if (background) {
                span.style.color = '#fff';
                doc.body.style.background = background;
            }
            doc.body.appendChild(span);
            doc.body.style.border = '0px';
            doc.body.style.margin = '0px';
            if (center) {
                doc.body.style.display = 'flex';
                doc.body.style.alignItems = 'center';
                doc.body.style.justifyContent = 'center';
            }
        }
    }

    showHylandErrorMessage(message: string) {
        const doc: Document = this.documentFrame.nativeElement.contentWindow ? this.documentFrame.nativeElement.contentWindow.document : null;
        if (doc) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.type = 'text/css';
            link.href = '//cdn.jsdelivr.net/fontawesome/4.7.0/css/font-awesome.min.css?ver=4.7.0';
            doc.head.appendChild(link);
            while (doc.body.firstChild) {
                doc.body.removeChild(doc.body.firstChild);
            }
            const docFrag = doc.createDocumentFragment();
            const icon = doc.createElement('i');
            icon.className = 'fa fa-info';
            icon.style.fontSize = '13px';
            icon.style.color = '#666';
            icon.style.backgroundColor = '#fff';
            icon.style.lineHeight = '30px';
            icon.style.paddingLeft = '15px';
            icon.style.paddingRight = '10px';
            docFrag.appendChild(icon);
            const span = doc.createElement('span');
            span.innerText = message;
            span.style.font = `400 13px 'Roboto', sans-serif`;
            span.style.color = '#666';
            span.style.padding = '0 5px';
            span.style.lineHeight = '30px';
            span.style.backgroundColor = '#fff';
            docFrag.appendChild(span);

            doc.body.appendChild(docFrag);
            doc.body.style.background = '#666';
            doc.body.style.border = '0px';
            doc.body.style.margin = '0px';
            doc.body.style.display = 'flex';
            doc.body.style.alignItems = 'center';
            doc.body.style.justifyContent = 'center';
        }
    }

    setDisplayFormat(format: DocumentFormat) {
        if (format === DocumentFormat.IMAGE_PDF && this.selectedDocument?.stored_image?.length === 0) {
            return;
        }
        this.displayFormat = format;
        this.loadDocument();
        this.documentTools.set();
    }

    spawnWindow(doc?: DrDocument, tab?: DocumentListTabType): void {
        const documentViewerContext = new DocumentViewerContext({
            selectedDocument: lo_.cloneDeep(doc ? doc : this.selectedDocument),
            displayFormat: this.displayFormat,
            documentListTab: tab ? tab : this.documentListTab,
            startIndex: (this.docListGrid && this.docListGrid.genericGrid && this.docListGrid.genericGrid.requestInfo
                && this.docListGrid.genericGrid.requestInfo.data) ? this.docListGrid.genericGrid.requestInfo.data.startIndex : 0,
            hideDuplicates: (this.docListGrid && this.docListGrid.genericGrid && this.docListGrid.genericGrid.requestInfo
                && this.docListGrid.genericGrid.requestInfo.data) ? this.docListGrid.genericGrid.requestInfo.data.suppressDuplicates : null,
            sortBy: (this.docListGrid && this.docListGrid.genericGrid && this.docListGrid.genericGrid.requestInfo
                && this.docListGrid.genericGrid.requestInfo.data) ? this.docListGrid.genericGrid.requestInfo.data.sortBy : null,
            sortAscending: (this.docListGrid && this.docListGrid.genericGrid && this.docListGrid.genericGrid.requestInfo
                && this.docListGrid.genericGrid.requestInfo.data) ? this.docListGrid.genericGrid.requestInfo.data.sortAscending : null,
            allMetadataFields: this.allMetadataFields,
            hideEmptyMetadata: this.hideEmptyMetadata,
            findInDocument: (this.documentTools.valid() || this.displayFormat === DocumentFormat.TEXT) ? this.documentTools.findInDocument.value : null,
            zoom: this.documentTools.valid() ? this.documentTools.zoom.currentZoom : null,
            rotation: this.documentTools.valid() ? this.documentTools.rotate.currentRotation : null,
            tagComment: this.tagComment,
            tagScope: this.tagScope,
            tagQueue: this.tagQueue
        });
        this.commonService.openExternalDocumentViewer(documentViewerContext);
    }

    onCloseDocumentViewer(isFromHome?: boolean): void {
        if (this.tagQueue.length > 0 && !this.commonService.isTagTaskInProgress) {
            this.commonService.openConfirmationModal('Pending changes in queue have not been applied.',
                'Unsaved Changes', 'Apply Changes', 'Cancel', 'Discard Changes', 'unsaved-changes-message').then(result => {
                    if (result.modalResult) {
                        if (result.modalData === 'Apply Changes') {
                            this.applyTagQueue(true);
                        }
                        this.closeDocumentViewer(isFromHome);
                    }
                });
        } else {
            this.closeDocumentViewer(isFromHome);
        }
    }

    closeDocumentViewer(isFromHome?: boolean): void {
        const documentViewerContext = new DocumentViewerContext();
        if (this.docListGrid && this.docListGrid.genericGrid && this.docListGrid.genericGrid.requestInfo && this.docListGrid.genericGrid.requestInfo.data) {
            documentViewerContext.startIndex = this.docListGrid.genericGrid.requestInfo.data.startIndex;
            documentViewerContext.hideDuplicates = this.docListGrid.genericGrid.requestInfo.data.suppressDuplicates;
            documentViewerContext.sortBy = this.docListGrid.genericGrid.requestInfo.data.sortBy;
            documentViewerContext.sortAscending = this.docListGrid.genericGrid.requestInfo.data.sortAscending;
        }
        this.commonService.documentViewerCloseContext = documentViewerContext;
        if (isFromHome) {
            this.commonService.clearData(true);
            this.httpService.stopSystemStatusPoller();
            this.router.navigate(['project-list'], { skipLocationChange: true });
        } else {
            this.router.navigate(['main-content'], { skipLocationChange: true });
        }
        this.commonService.docViewerOpened = false;
    }

    showMetadata(): boolean {
        if (this.leftResizer && typeof this.leftResizer.collapsed === 'boolean') {
            return !this.leftResizer.collapsed;
        }
        return false;
    }

    showMetadataChanged(): void {
        this.leftResizer.toggleCollapse();
    }

    setDocumentListTab(tab: DocumentListTabType, selectItem = true) {
        this.documentListTab = tab;
        this.currentDocumentList = null;
        this.documentListBreadcrumbs = [new CustomSelectItem({
            name: this.selectedNavigationNode.name,
            icon: 'fa-list', iconColor: 'color-blue-01', iconColorDisabled: 'color-blue-grey-04',
            type: DocumentListTabType.docList
        })];
        this.selectedDocumentListBreadcrumb = this.documentListBreadcrumbs[0];
        switch (tab) {
            case DocumentListTabType.family: {
                this.currentDocumentList = this.familyGrid;
                this.documentListBreadcrumbs.push(new CustomSelectItem({
                    name: 'Family of ' + this.selectedDocument.displayName,
                    icon: 'fa-paperclip', iconColor: 'color-green-04', level: 1,
                    type: DocumentListTabType.family
                }));
                this.selectedDocumentListBreadcrumb = this.documentListBreadcrumbs[1];
                break;
            }
            case DocumentListTabType.thread: {
                if (!this.threadGrid.requestInfo) {
                    this.loadThreads();
                }
                this.documentListBreadcrumbs.push(new CustomSelectItem({
                    name: 'Thread of ' + this.selectedDocument.displayName,
                    icon: this.documentViewerContext.isShortMessageThreads ? 'fa-comments-o' : 'fa-envelope', iconColor: 'color-blue-grey-03',
                    iconSecond: 'fa-link', iconSecondColor: 'color-blue-01',
                    level: 1,
                    type: DocumentListTabType.thread
                }));
                this.selectedDocumentListBreadcrumb = this.documentListBreadcrumbs[1];
                this.currentDocumentList = this.threadGrid;
                break;
            }
            case DocumentListTabType.docList: {
                this.currentDocumentList = this.docListGrid;
                break;
            }
        }
        this.moveToDocument.disabled.backward = true;
        this.moveToDocument.disabled.forward = true;
        if (this.currentDocumentListApi && selectItem) {
            const selectedItem = this.currentDocumentListApi.getSelectedRows()[0];
            if (selectedItem instanceof DrDocument) {
                this.setSelectedDocument(selectedItem);
            }
        }

        if (this.middleResizer && this.middleResizer.collapsed) {
            this.middleResizer.toggleCollapse();
        }
    }

    selectedDocumentListBreadcrumbChanged = (item: CustomSelectItem) => {
        if (item && <DocumentListTabType>item.type) {
            this.setDocumentListTab(<DocumentListTabType>item.type);
        }
    }

    loadData(): void {
        if (!this.selectedDocument.isThread) {
            this.loadMetadata();
            this.requestInfo.history.data = { handle: this.selectedDocument.handle };
            this.historyGrid.loadData();
        }
        if (this.selectedDocument.hasFamily && (!this.selectedDocument.isAttachment || this.commonService.docViewerOpened)) {
            const nodeData = { handle: this.selectedDocument.handle };
            if (this.selectedNavigationNode && this.selectedNavigationNode.data && this.selectedNavigationNode.data.viewType) {
                Object.assign(nodeData, { viewType: this.selectedNavigationNode.data.viewType });
            }
            this.familyGrid.selectedTreeItemChanged(new GenericTreeNode({
                entityType: this.selectedNavigationNode.entityType,
                data: nodeData
            }), true);
        }
    }

    loadThreads(): void {
        if (this.selectedDocument.hasThread || this.selectedDocument.isThread) {
            if (this.threadGrid.genericGrid) {
                this.threadGrid.genericGrid.isBusy = true;
            }
            const threadHandle = this.selectedDocument.isThread ? this.selectedDocument.handle :
                ((this.selectedNavigationNode.data.docListHandle ? this.selectedNavigationNode.data.docListHandle :
                    (this.selectedNavigationNode.data.view ? this.selectedNavigationNode.data.view.handle : this.selectedNavigationNode.data.handle)) + ',' + this.selectedDocument.thread_handle);
            const requestInfo = {
                workingSetHandle: this.selectedNavigationNode.data.handle,
                selectedAttributes: this.docListGrid.requestInfo.data.selectedAttributes,
                threadHandle: threadHandle
            };
            this.restService.post(Endpoint.workingSetViewManager_listEmailsInThread, requestInfo).then(response => {
                if (response && response.handle) {
                    this.threadGrid.requestInfo = new DrRequestInfo({
                        endpoint: Endpoint.workingSetViewManager_listEmailsInThreadResult,
                        responseName: 'documents',
                        data: { handle: response.handle }
                    });
                    this.threadGrid.loadData();
                }
            }, error => {
                if (this.threadGrid.genericGrid) {
                    this.threadGrid.genericGrid.isBusy = false;
                }
            });
        }
    }

    loadMetadata(): void {
        if (this.metadataGrid && this.metadataGrid.gridOptions) {
            this.metadataGrid.gridOptions.context.sortBy = this.allMetadataFields ? 'key' : null;
        } else {
            this.gridContext.metadata.sortBy = this.allMetadataFields ? 'key' : null;
        }
        this.isBusyMessage.metadata = 'Loading Metadata ...';
        this.isBusy.metadata = true;
        if (!this.metadataTokenizations) {
            this.metadataService.tokenizationTypes().then(responseTokenizations => {
                this.metadataTokenizations = responseTokenizations;
            });
        }
        if (!this.metadataFieldDescriptions) {
            this.metadataService.fieldDescriptions().then(responseDescriptions => {
                this.metadataFieldDescriptions = responseDescriptions ? responseDescriptions : [];
                this.loadMetadata();
            }, error => {
                this.isBusy.metadata = false;
            });

        } else {
            this.restService.post(Endpoint.viewManager_getDocumentInfo, {
                handle: this.selectedDocument.handle,
                applyMetadataTemplate: !this.allMetadataFields,
                showEmptyFields: !this.hideEmptyMetadata
            }).then(response => {
                const metadata = new Array<DocumentViewerMetadataField>();
                if (response && response.documentInfo) {
                    this.metadataXml = response.documentInfo;
                    const metadataDocumentInfo = jquery.parseXML(this.metadataXml);
                    if (metadataDocumentInfo.childNodes && metadataDocumentInfo.childNodes.length === 1 && metadataDocumentInfo.childNodes[0].childNodes &&
                        metadataDocumentInfo.childNodes[0].childNodes.length > 0) {
                        for (let index = 0; index < metadataDocumentInfo.childNodes[0].childNodes.length; index++) {
                            const children = Array.from<any>(metadataDocumentInfo.childNodes[0].childNodes[index].childNodes);
                            this.parseMetadata(metadata, children);
                        }
                    }
                }
                const grouped = [];
                _.each(_.groupBy(metadata, (data) => data.key), (data) => {
                    if (data.length > 24) {
                        const unshown = formatNumber(data.length - 24, navigator.language);
                        data[24].displayValue = `${unshown} more ...`;
                        data[24].displayValueTooltip = `Download the metadata to see all values`;
                        data[24].isButtonVisible = false;
                        data = data.slice(0, 25);
                    }
                    grouped.push(...data);
                });
                this.metadataGrid.loadAndFilter = true;
                this.metadataGrid.setData(grouped);
                this.isBusy.metadata = false;
            }, error => {
                this.isBusy.metadata = false;
            });
        }
    }

    parseMetadata(metadata: Array<DocumentViewerMetadataField>, nodes: Array<any>, parent: DocumentViewerMetadataField = null) {
        if (isArray(nodes) && nodes.length > 0) {
            for (let index = 0; index < nodes.length; index++) {
                const node = nodes[index];
                let key = node.localName || '';
                key = key.replace(/[^\w\s]/gi, '').replace(/ /g, '');
                const hasAlt = Array.from<any>(node.attributes).filter(c => c.localName && c.localName === 'hasAlt');
                const drLocationKey = 'dr_document_location';
                if (node.childNodes.length === 0 || (node.childNodes.length === 1 && node.childNodes[0].nodeName === '#text')) {
                    const desc = this.metadataFieldDescriptions.filter(d => d.name === key).pop();
                    if (key === 'dr_loc') {
                        key = drLocationKey;
                    }
                    const metadataField = new DocumentViewerMetadataField({
                        key: key,
                        value: key?.localeCompare('password') !== 0 ? node.textContent.trim() : node.textContent,
                        keyTooltip: desc ? desc.value : null
                    });
                    if (parent) {
                        metadataField.level = parent.level + 1;
                        parent.children.push(metadataField);
                    } else {
                        metadata.push(metadataField);
                    }
                } else if (hasAlt.length > 0 && node.childNodes.length > 0) {
                    // In the case of a hasAlt attribute, display the child "v" value (and hide the other children)
                    const children = Array.from<any>(node.childNodes).filter(c => c.localName && (c.localName === 'v' || c.localName === 'a'));
                    const desc = this.metadataFieldDescriptions.filter(d => d.name === key).pop();
                    if (hasAlt.length > 0 && hasAlt[0].value === 'true' && children.length) {
                        const metadataField = new DocumentViewerMetadataField({
                            key: key,
                            value: children[0].textContent,
                            keyTooltip: desc ? desc.value : null,
                            isParent: true,
                            isExpanded: true,
                            children: new Array<DocumentViewerMetadataField>()
                        });
                        if (parent) {
                            metadataField.level = parent.level + 1;
                            parent.children.push(metadataField);
                        } else {
                            metadata.push(metadataField);
                        }
                        this.parseMetadata(metadata, children, metadataField);
                    }
                } else if (node.childNodes.length > 0) {
                    const children = Array.from<any>(node.childNodes).filter(c => c.localName && c.localName !== 'a' && c.localName !== 'v');
                    if (children.length > 0) {
                        const desc = this.metadataFieldDescriptions.filter(d => d.name === key).pop();
                        if (key === 'dr_loc') {
                            key = drLocationKey;
                        }
                        const metadataField = new DocumentViewerMetadataField({
                            key: key,
                            value: '',
                            keyTooltip: desc ? desc.value : null,
                            isParent: true,
                            isExpanded: true,
                            children: new Array<DocumentViewerMetadataField>()
                        });
                        if (parent) {
                            metadataField.level = parent.level + 1;
                            parent.children.push(metadataField);
                        } else {
                            metadata.push(metadataField);
                        }
                        this.parseMetadata(metadata, children, metadataField);
                    }
                }
            }
        }
    }

    downloadMetadata(): void {
        if (this.metadataXml) {
            const bb = new Blob([this.metadataXml], { type: 'text/xml' });
            const fileNameWithoutExtension = this.selectedDocument ? this.getFileNameWithoutExtension(this.selectedDocument.filename) : null;
            this.downloadLink.nativeElement.download = fileNameWithoutExtension ? fileNameWithoutExtension + '_metadata.xml' : 'metadata.xml';
            this.downloadLink.nativeElement.href = window.URL.createObjectURL(bb);
            this.downloadLink.nativeElement.click();
        }
    }

    onCustomFilterCallback = (event?: SelectionChangedEvent) => {
        if (this.metadataGrid.initialLoadData) {
            this.metadataGrid.loadAndFilter = true;
            this.metadataGrid.setData(this.metadataGrid.initialLoadData);
        }
    }

    getFileNameWithoutExtension(fileName: string): string {
        if (fileName) {
            const pos = fileName.lastIndexOf('.');
            if (pos === -1) {
                return fileName;
            }
            return fileName.substring(0, pos);
        }
        return null;
    }

    loadTags(newTagResponse?: any): void {
        this.isBusy.tags = true;
        this.isBusyMessage.tags = 'Loading Tags ...';
        this.restService.post(Endpoint.workingSetManager_listWorkingSetViews, {
            workingSetHandle: this.commonService.projectData.handle,
            filters: [{
                attribute: 'viewType',
                value: 'TAG',
                operator: 'EQUALS'
            }]
        }).then(response => {
            this.isBusy.tags = false;
            if (response && response.views) {
                this.tagList = (<Array<any>>response.views).map(t => new Tag(t));
                this.setTags(newTagResponse);
            }
        }, error => {
            this.isBusy.tags = false;
        });
    }

    setTags(newTagResponse?: any): void {
        if (this.selectedDocument && this.tagList) {
            this.tagList.forEach(tag => {
                tag.selected = false;
                if (this.selectedDocument.tagList && this.selectedDocument.tagList.find(taggedItem => taggedItem.handle === tag.handle)) {
                    tag.selected = true;
                }
                const qItem = this.tagQueue.find(q => q.documentNumber === this.selectedDocument.docnum);
                if (qItem) {
                    const qta = qItem.tagList.find(t => t.tagHandle === tag.handle);
                    if (qta) {
                        tag.selected = qta.tagState === TagState.ON;
                    }
                }
                if (newTagResponse && newTagResponse.tag && newTagResponse.tag.handle === tag.handle) {
                    tag.selected = true;
                    this.tagSelected(tag);
                }
            });
            if (this.tagQueueContext && this.tagQueueGrid && this.tagQueueGrid.gridApi) {
                this.tagQueue = this.tagQueueContext;
                this.tagQueueGrid.setData(this.tagQueue);
                this.tagQueueContext = null;
                const qItem = this.tagQueue.find(q => q.documentHandle === this.selectedDocument.handle);
                if (qItem) {
                    this.tagList.forEach(tag => {
                        const qta = qItem.tagList.find(t => t.tagHandle === tag.handle);
                        if (qta) {
                            tag.selected = qta.tagState === TagState.ON;
                        }
                    });
                }
            }
        }
    }

    tagSelected(tag: Tag): void {
        if (this.selectedDocument && tag) {
            const qItem = this.tagQueue.find(q => q.documentHandle === this.selectedDocument.handle);
            const ta = new TagQueueApplication({
                tagState: tag.selected ? TagState.ON : TagState.NONE,
                tagHandle: tag.handle,
                tagName: tag.name,
                displayValue: tag.displayValue
            });
            if (qItem) {
                const qta = qItem.tagList.find(t => t.tagHandle === tag.handle);
                if (qta) {
                    qItem.tagList = _.without(qItem.tagList, qta);
                    if (qItem.tagList.length === 0) {
                        this.tagQueue = _.without(this.tagQueue, qItem);
                    }
                } else {
                    qItem.tagList.push(ta);
                }
            } else {
                this.tagQueue.push(new TagQueueItem({
                    documentHandle: this.selectedDocument.handle,
                    documentNumber: this.selectedDocument.docnum,
                    documentNumberTooltip: this.selectedDocument.displayName,
                    tagList: new Array<TagQueueApplication>(ta)
                }));
            }
            this.tagQueueGrid.setData(this.tagQueue);
        }
    }

    newTag(): void {
        this.commonService.openModal(EditBucketModalComponent, { modalData: { mode: EditMode.TAG } }).then((value) => {
            if (value.modalResult) {
                this.loadTags(value.modalData);
            }
        });
    }

    clearTagQueue(): void {
        this.commonService.openModal(DeleteEntityModalComponent, { modalData: new TagQueueApplication({}) }).then(response => {
            if (response.modalResult) {
                this.tagQueue = [];
                this.tagQueueGrid.setData(this.tagQueue);
                this.setTags();
            }
        });
    }

    applyTagQueue(closing?: boolean): void {
        this.isBusy.tags = true;
        this.isBusyMessage.tags = 'Tagging...';
        this.commonService.isTagTaskInProgress = false;
        const request = {
            destinationWorkingSetHandle: this.commonService.projectData.handle,
            taskDescription: 'Tagging ' + (this.tagQueue.length > 1 ? 'documents' : 'document'),
            tagOperations: this.tagQueue.map(tqi => new TagDocumentsOperation({
                tagList: tqi.tagList.map(tli => new TagApplication({
                    tagHandle: tli.tagHandle,
                    tagState: tli.tagState
                })),
                handles: [tqi.documentHandle],
                scope: this.tagScope,
                comment: this.tagComment
            }))
        };
        const applyCall = this.restService.post(Endpoint.viewManager_tagDocuments, request);
        if (applyCall && !closing) {
            applyCall.then(response => {
                this.isBusy.tags = false;
                this.commonService.isTagTaskInProgress = false;
                this.tagQueue = [];
                this.tagComment = '';
                this.tagQueueGrid.setData(this.tagQueue);
                if (this.selectedDocument.hasThread || this.selectedDocument.isThread) {
                    this.loadThreads();
                }
                if (this.selectedDocument.hasFamily && this.documentListTab !== DocumentListTabType.family) {
                    this.familyGrid.loadData();
                }
                if (this.documentListTab !== DocumentListTabType.docList) {
                    this.docListGrid.loadData();
                }
                if (this.currentDocumentList) {
                    this.currentDocumentList.loadData();
                }
                this.requestInfo.history.data = { handle: this.selectedDocument.handle };
                this.historyGrid.loadData(0, 0, true);
            }, error => {
                this.isBusy.tags = false;
                this.commonService.isTagTaskInProgress = false;
            });
        }
    }

    selectFindInDocumentTextTab(offset): void {
        const iFrameSource = this.documentFrame.nativeElement.contentDocument.body;

        if (iFrameSource) {
            const matchArr = iFrameSource.getElementsByTagName('mark');

            this.documentTools.findInDocument.resultIndex += offset;
            this.documentTools.findInDocument.resultIndex = this.documentTools.findInDocument.resultIndex % matchArr.length;

            if (this.documentTools.findInDocument.resultIndex === -1 || this.documentTools.findInDocument.resultIndex === -2) {
                this.documentTools.findInDocument.resultIndex = matchArr.length - 1;
            }
            if (!isNaN(this.documentTools.findInDocument.resultIndex)) {
                matchArr[this.documentTools.findInDocument.resultIndex].scrollIntoView({ behavior: 'instant', block: 'center' });
            }

            if (matchArr && matchArr.length > 0) {
                for (let i = 0; i < matchArr.length; i++) {
                    matchArr[i].style.border = (this.documentTools.findInDocument.resultIndex === i) ? '2px solid #3399EE' : '0px solid #D2FF00';
                    matchArr[i].style.backgroundColor = '#D2FF00';
                }
            }
        }

    }

    openHelp = () => this.commonService.openHelp(HelpIndex.ediscovery_document_viewer_5x_dlg);

    ngOnDestroy(): void {
        this.commonService.documentViewerOpenContext = null;
        if (this.closeDocumentViewerRequestEvent) {
            this.closeDocumentViewerRequestEvent.unsubscribe();
        }
    }
}
